<template>
    <div class="w-full">
        <!-- Main Song Header -->
        <div class="rounded-lg overflow-hidden mb-8 p-6 md:p-8"
            :style="{ 'backgroundColor': backgroundFromBlurhash(song.blur_hash) }">
            <div class="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
                <!-- Song Cover Image -->
                <div class="md:col-span-4 text-center md:text-left">
                    <img :src="imageCover" :alt="song.name + ' by ' + song.artist.name"
                        :title="song.name + ' by ' + song.artist.name"
                        class="rounded-lg w-full shadow-lg mx-auto md:mx-0" width="490" height="294" loading="eager"
                        fetchpriority="high" />
                </div>

                <!-- Song Info -->
                <div class="md:col-span-8 space-y-3">
                    <!-- Song Title -->
                    <h1 class="text-white font-bold text-3xl md:text-4xl leading-tight">{{ song.name }}</h1>

                    <!-- Artist Info -->
                    <div class="space-y-1">
                        <h2 class="text-lg text-nomad-200">
                            <Link :href="'/artist/' + song.artist.id"
                                class="text-nomad-accent hover:text-nomad-accent/80 transition-colors">
                                {{ song.artist.name }}
                            </Link>
                            <featuring :artists="song.featuring" />
                        </h2>
                        <div v-if="song.origin_artist" class="text-nomad-300 text-sm">
                            Original vocalist:
                            <Link :href="'/artist/' + song.origin_artist.id"
                                class="text-nomad-accent hover:text-nomad-accent/80 transition-colors">
                                {{ song.origin_artist.name }}
                            </Link>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-wrap gap-3 pt-2">
                        <!-- Play Button -->
                        <SmartPlay :song="song" v-if="song.meta && song.meta.music" />
                        <button v-else aria-label="play song" title="play song"
                            @click="prepareCurrent({ id: song.id, type: 'SONG' })"
                            class="group flex items-center gap-2 px-6 py-3 bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-lg transition-all duration-300 hover:scale-105">
                            <span class="icon-play text-lg"></span>
                            <div class="flex flex-col relative">
                                <span class="leading-tight">Play</span>
                                <span v-if="song.formatted_duration"
                                    class="text-xs font-normal leading-tight absolute top-full left-0 hidden group-hover:block transition-all whitespace-nowrap">
                                    ({{ song.formatted_duration }})
                                </span>
                            </div>
                        </button>

                        <!-- Like Button -->
                        <button @click="addToFavorites(song.id)"
                            :class="song.is_liked ? 'text-nomad-accent' : 'text-nomad-300 hover:text-nomad-accent'"
                            title="like" aria-label="like"
                            class="p-3 rounded-lg transition-all icon-heart text-xl hover:scale-110 hover:shadow-md">
                        </button>

                        <!-- YouTube Button -->
                        <button v-if="song.youtube" @click="openYoutube" aria-label="youtube video"
                            title="youtube video"
                            class="p-3 rounded-lg text-nomad-300 hover:text-nomad-accent transition-all icon-youtube text-xl hover:scale-110 hover:shadow-md">
                        </button>

                        <!-- Share Button -->
                        <button v-if="song.id" @click="copyLink" title="share url" aria-label="share url"
                            class="p-3 rounded-lg text-nomad-300 hover:text-nomad-accent transition-all icon-link text-xl hover:scale-110 hover:shadow-md">
                        </button>

                        <!-- Lyrics Button -->
                        <button @click="addLyrics" title="add lyrics" aria-label="add lyrics"
                            :class="song.verse && song.verse.length && song.verse[0].is_default ? 'text-nomad-green' : 'text-nomad-300 hover:text-nomad-accent'"
                            class="p-3 rounded-lg transition-all icon-file-text text-xl hover:scale-110 hover:shadow-md">
                        </button>

                        <!-- External YouTube Link -->
                        <a v-if="song.youtube" :href="'https://www.youtube.com/watch?v=' + song.youtube"
                            aria-label="watch on youtube" title="watch on youtube" target="_blank"
                            class="p-3 rounded-lg text-nomad-300 hover:text-nomad-accent transition-all icon-external-link text-xl hover:scale-110 hover:shadow-md">
                        </a>
                    </div>
                    <div v-if="song.playlists && song.playlists.length" class="pt-2">
                        <h3 class="text-sm font-semibold text-white mb-2">In Playlists</h3>
                        <div class="flex flex-wrap gap-2">
                            <Link v-for="playlist in song.playlists" :key="playlist.id" :href="'/playlist/' + playlist.id"
                                class="inline-block text-nomad-200 text-sm rounded-lg bg-nomad-800 hover:bg-nomad-700 px-3 py-2 transition-all hover:shadow-md">
                                #{{ playlist.name }}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
            <div class="order-2 lg:order-1 lg:col-span-7 space-y-8">
                <section v-if="song.similarSongs && song.similarSongs.length">
                    <h2 class="text-2xl font-bold text-white mb-6">Similar Songs</h2>
                    <ul class="grid grid-cols-1 gap-4">
                        <li v-for="songContent in song.similarSongs" :key="songContent.id">
                            <card-song :song="songContent" />
                        </li>
                    </ul>
                </section>
            </div>

            <div class="order-1 lg:order-2 lg:col-span-5">
                <section v-if="lyricsText">
                    <h2 class="text-xl font-bold text-white mb-4">Lyrics</h2>
                    <p class="text-nomad-200 leading-8 whitespace-pre-line" dir="auto">{{ lyricsText }}</p>
                </section>
            </div>
        </div>

        <section class="w-full border-t border-nomad-800 pt-6 mb-8">
            <h3 class="text-xl font-bold text-white mb-4">Get the App</h3>
            <div class="flex flex-wrap gap-3">
                <a href="https://play.google.com/store/apps/details?id=com.nomadlyrics&utm_source=nomadlyrics.com&utm_source_platform=desktop"
                    title="Android App" target="_blank"
                    class="inline-block px-4 py-2 bg-nomad-800 hover:bg-nomad-700 text-nomad-accent rounded-lg text-sm font-medium transition-all hover:shadow-md">
                    Android App
                </a>
                <a href="https://apps.apple.com/app/apple-store/id1503789749?pt=121219390&ct=NomadLyricsWebsite&mt=8"
                    title="iOS App" target="_blank"
                    class="inline-block px-4 py-2 bg-nomad-800 hover:bg-nomad-700 text-nomad-accent rounded-lg text-sm font-medium transition-all hover:shadow-md">
                    Apple App
                </a>
            </div>
        </section>
    </div>
    <Youtube :watching="watchVideo" @open-youtube="openYoutube" :id="song.youtube" v-if="watchVideo" />
    <lyrics-add :song="song" :id="lyricsId" @open-lyrics="addLyrics" @lyrics-edit-id="lyricsEditId"
        v-if="addingLyrics"></lyrics-add>
</template>
<script setup>
defineProps({ song: Object });
</script>

<script>
import CardSong from "@/components/CardSong.vue";
import Featuring from "@/components/Featuring.vue";
import { Link, usePage } from "@inertiajs/vue3";
import { getBlurHashAverageColor } from 'fast-blurhash';

import { mapState, mapActions } from "vuex";

import { defineAsyncComponent } from 'vue'
import SmartPlay from "@/components/SmartPlay.vue";

const page = usePage();
export default {
    data() {
        return {
            watchVideo: false,
            addingLyrics: false,
            lyricsId: null
        };
    },
    beforeMount() {
        document.title = page.props.title
    },
    methods: {
        ...mapActions("favorites", ["addToFavorites"]),
        ...mapActions("current", ["prepareCurrent"]),
        ...mapActions("notification", ["showNotification"]),
        openYoutube() {
            this.watchVideo = !this.watchVideo;
        },
        async copyLink() {
            navigator.clipboard.writeText("https://nomadlyrics.com/song/" + this.song.id);
            this.showNotification({
                message: 'link coppied!',
                type: 'info',
            })
            trackShare('Link', 'song', this.song.id)
        },
        addLyrics() {
            this.addingLyrics = !this.addingLyrics;
            this.$gtag.event("select_content", {
                content_type: "LYRICS::INIT",
                content_id: this.song.id
            });
        },
        lyricsEditId(id) {
            this.lyricsId = id;
        },
        trackShare(platform, type, id) {
            // Track the "SongPlay" event with the associated ID
            this.$gtag.event('share', {
                method: platform, //Facebook, Twitter, etc
                content_type: type, // image, song, event, episode
                song_id: id,
            });

        },
        backgroundFromBlurhash(blurhash) {
            if (!blurhash) {
                return '#1F201D'; // default background color
            }

            const rgbAverageColor = getBlurHashAverageColor(blurhash);
            return `rgba(${rgbAverageColor.join(',')}, 0.3)`; // adding some transparency

        },

    },
    components: {
        "card-song": CardSong,
        featuring: Featuring,
        Link,
        Promo: defineAsyncComponent(() => import('@/components/Promo')),
        'Youtube': defineAsyncComponent(() => import('@/components/Youtube.vue')),
        'adsense': defineAsyncComponent(() => import("@/components/Adsense.vue")),
        'lyrics-add': defineAsyncComponent(() => import('@/components/LyricsAdd.vue')),
        'adsense-responsive': defineAsyncComponent(() => import('@/components/AdsenseResponsive.vue')),
    },
    computed: {
        lyricsText() {
            const verses = Array.isArray(this.song?.verse) ? this.song.verse : [];
            const submission = verses.find((verse) => verse.is_default && verse.lyrics?.non_synced_lyrics?.original)
                || verses.find((verse) => verse.lyrics?.non_synced_lyrics?.original);

            return submission?.lyrics?.non_synced_lyrics?.original || '';
        },
        imageCover() {
            if (this.song.meta && this.song.meta.image) return this.song.meta.image;
            if (this.song.cover) return this.song.cover;
            return this.song.artist.image;
        },
    },
};
</script>
